import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import DialogHeader from "./DialogHeader";
import { EventAvailable } from "@mui/icons-material";
import type { OneOffSession, OneOffSessionBookingMode } from "./types";
import { updateOneOffSessionBooking } from "./api";

interface SessionBookingSettingsDialogProps {
  session: OneOffSession | null;
  onClose: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
}

const SessionBookingSettingsDialog: React.FC<
  SessionBookingSettingsDialogProps
> = ({ session, onClose, onSuccess, onError }) => {
  const [bookingMode, setBookingMode] = useState<OneOffSessionBookingMode>("off");
  const [capacity, setCapacity] = useState("");
  const [fee, setFee] = useState("0.00");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!session) return;
    setBookingMode(session.booking_mode ?? "off");
    setCapacity(
      session.capacity != null && session.capacity > 0
        ? String(session.capacity)
        : "",
    );
    setFee((session.fee ?? 0).toFixed(2));
  }, [session]);

  const handleSubmit = async () => {
    if (!session) return;
    const parsedFee = parseFloat(fee);
    if (Number.isNaN(parsedFee) || parsedFee < 0) {
      onError("Please enter a valid fee.");
      return;
    }

    let cap: number | undefined;
    if (bookingMode !== "off") {
      cap = parseInt(capacity, 10);
      if (Number.isNaN(cap) || cap < 1) {
        onError("Capacity must be at least 1 when booking is enabled.");
        return;
      }
      const taken = session.spaces_taken ?? session.participants?.length ?? 0;
      if (cap < taken) {
        onError(`Capacity cannot be less than current bookings (${taken}).`);
        return;
      }
    }

    try {
      setSubmitting(true);
      await updateOneOffSessionBooking({
        one_off_session_id: session.id,
        booking_mode: bookingMode,
        capacity: bookingMode !== "off" ? cap : undefined,
        fee: parsedFee,
      });
      onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      onError("Failed to update booking settings.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={session != null} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogHeader
        title="Session booking"
        icon={<EventAvailable color="primary" />}
        onClose={onClose}
      />
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Unpaid self-bookings are automatically cancelled after 24 hours and
          the place is released.
        </Alert>
        <FormControl component="fieldset" sx={{ mb: 2, width: "100%" }}>
          <RadioGroup
            value={bookingMode}
            onChange={(e) =>
              setBookingMode(e.target.value as OneOffSessionBookingMode)
            }
          >
            <FormControlLabel
              value="off"
              control={<Radio />}
              label="Closed — admin adds participants manually"
            />
            <FormControlLabel
              value="private"
              control={<Radio />}
              label="Link only — members book via share link"
            />
            <FormControlLabel
              value="public"
              control={<Radio />}
              label="Open to all club members (and via link)"
            />
          </RadioGroup>
        </FormControl>
        {bookingMode !== "off" && (
          <>
            <TextField
              label="Capacity (places)"
              type="number"
              fullWidth
              margin="normal"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Fee per place (£)"
              fullWidth
              margin="normal"
              value={fee}
              onChange={(e) => setFee(e.target.value)}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionBookingSettingsDialog;
